import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import Tooltip from '@components/common/tooltip/Tooltip';
import { IApplicant } from '../../types';
import { isValidAmount, getEligibilityRowClassName } from '../../utils';
import AmountDisplay from './AmountDisplay';

export interface IEligibilityResultsSectionProps {
    applicants?: IApplicant[];
}
// ============================================================================
// Component
// ============================================================================

const EligibilityResultsSection: React.FC<IEligibilityResultsSectionProps> = ({ applicants }) => {
    const results = applicants?.map(applicant => {
        const bewellId = applicant.bewellMemberId != null ? String(applicant.bewellMemberId) : '';
        const ssnId = applicant.ssnLastFour || '';
        const nameId = `${applicant.firstName || ''}-${applicant.lastName || ''}`.trim();
        const memberId = bewellId || ssnId || nameId || 'unknown';
        const eligibleForAptc = applicant.eligibleForAptc;
        const eligibleForStateSubsidy = applicant.eligibleForStateSubsidy;
        const eligibleForQhp = applicant.eligibleForQhp;
        const aptcAmount = applicant.aptcAmount != null ? String(applicant.aptcAmount) : undefined;
        const premiumAssistanceAmount = applicant.stateSubsidyAmount != null ? String(applicant.stateSubsidyAmount) : undefined;
        const eligibleForNapa = applicant.eligibleForNapa;
        const hasCostSharingReductions = !!applicant.csrLevel;
        const hasNoEligibilities =
            eligibleForNapa === false &&
            eligibleForStateSubsidy === false &&
            eligibleForAptc === false &&
            eligibleForQhp === false &&
            !hasCostSharingReductions;
        const isNotEligible = hasNoEligibilities;
        return {
            memberId,
            memberName: `${applicant.firstName || ''} ${applicant.lastName || ''}`.trim(),
            aptcAmount,
            premiumAssistanceAmount,
            hasCostSharingReductions,
            eligibleForAptc,
            eligibleForStateSubsidy,
            eligibleForQhp,
            eligibleForNapa,
            isNotEligible,
        };
    }) || [];

    return (
        <div className="padding-3 margin-bottom-3 border-1px border-base-lighter radius-md" aria-label="Eligibility Results" aria-live="polite">
            <h4 className="margin-top-0 margin-bottom-3">
                <FormattedMessage id="historical.member.applications.eligibilityResults" />
            </h4>
            {results?.map((result, index) => (
                <div key={result.memberId} className={`historical-eligibility-results-row ${getEligibilityRowClassName(index, results.length)}`}>
                    <div className="margin-0 historical-eligibility-results-name">{result.memberName}</div>
                    <div className="text-left historical-eligibility-results-info">
                        <ConditionalRendering isTrue={result.isNotEligible}>
                            <FormattedMessage id="common.not_eligible" />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={!result.isNotEligible}>
                            <ConditionalRendering isTrue={result.eligibleForQhp}>
                                <FormattedMessage id="historical.member.applications.qhp" />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={result.eligibleForAptc && isValidAmount(result.aptcAmount)}>
                                <AmountDisplay
                                    amount={result.aptcAmount!}
                                    labelId="historical.member.applications.advancedPremiumTaxCredit"
                                    tooltipContent="household_section.tooltip_aptc"
                                    tooltipIndex={`aptc-${result.memberId}`}
                                />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={result.eligibleForStateSubsidy && isValidAmount(result.premiumAssistanceAmount)}>
                                <AmountDisplay
                                    amount={result.premiumAssistanceAmount!}
                                    labelId="historical.member.applications.statePremiumAssistance"
                                    tooltipContent="household_section.tooltip_state_subsidy"
                                    tooltipIndex={`nmpa-${result.memberId}`}
                                />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={result.eligibleForNapa}>
                                <div>
                                    <Tooltip
                                        id={<FormattedMessage id="historical.member.applications.napa" />}
                                        content="householdSection.tooltipStateSubsidy2"
                                        index={`napa-${result.memberId}`}
                                    />
                                </div>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={result.hasCostSharingReductions}>
                                <div className="text-base-dark margin-top-1">
                                    <FormattedMessage id="historical.member.applications.oneOrMoreMember" />&nbsp; 
                                    <FormattedMessage id="historical.member.applications.eligibleFor" />&nbsp; 
                                    <Tooltip
                                        id={<FormattedMessage id="historical.member.applications.costSharingReductions" />}
                                        content="household_section.tooltip_csr"
                                        index={`csr-${result.memberId}`}
                                    />
                                </div>
                            </ConditionalRendering>
                        </ConditionalRendering>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EligibilityResultsSection;

